import { useState, useEffect, useCallback, useRef } from 'react'
import { customerApi } from '../services/api'
import toast from 'react-hot-toast'
import InboxSearch from '../components/inbox/InboxSearch'
import InboxFilters from '../components/inbox/InboxFilters'
import ConversationList from '../components/inbox/ConversationList'
import ConversationDetail from '../components/inbox/ConversationDetail'
import BulkActionBar from '../components/inbox/BulkActionBar'
import ConfirmDialog from '../components/common/ConfirmDialog'
import { ArrowPathIcon, InboxIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'

const DEFAULT_FILTERS = { status: 'all', dateFrom: null, dateTo: null, agent: null, search: '', source: null }

export default function Inbox() {
  const [conversations, setConversations] = useState([])
  const [selectedConv, setSelectedConv] = useState(null)
  const [messages, setMessages] = useState([])
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loadingList, setLoadingList] = useState(true)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [stats, setStats] = useState({})
  const [checkedIds, setCheckedIds] = useState(new Set())
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [mobileShowDetail, setMobileShowDetail] = useState(false)
  const pollRef = useRef(null)
  const detailPollRef = useRef(null)

  // Fetch conversations
  const fetchConversations = useCallback(async (pageNum = 1, append = false) => {
    if (!append) setLoadingList(true)
    try {
      const params = new URLSearchParams({ page: pageNum, per_page: 20 })
      if (filters.status !== 'all') params.append('status', filters.status)
      if (filters.search) params.append('search', filters.search)
      if (filters.dateFrom) params.append('date_from', filters.dateFrom)
      if (filters.dateTo) params.append('date_to', filters.dateTo)
      if (filters.agent) params.append('agent', filters.agent)
      if (filters.source) params.append('source', filters.source)

      const res = await customerApi.get(`/customer/inbox/conversations?${params}`)
      const data = res.data
      if (append) {
        setConversations((prev) => [...prev, ...data.conversations])
      } else {
        setConversations(data.conversations)
      }
      setHasMore(data.pagination.page < data.pagination.total_pages)
    } catch (err) {
      toast.error('Failed to load conversations')
    } finally {
      setLoadingList(false)
    }
  }, [filters])

  const fetchStats = async () => {
    try {
      const res = await customerApi.get('/customer/inbox/stats')
      setStats(res.data)
    } catch {}
  }

  // Initial load
  useEffect(() => {
    setPage(1)
    fetchConversations(1)
    fetchStats()
  }, [fetchConversations])

  // Polling for stats every 30s
  useEffect(() => {
    pollRef.current = setInterval(() => fetchStats(), 30000)
    return () => clearInterval(pollRef.current)
  }, [])

  // Fetch detail
  const selectConversation = async (conv) => {
    setSelectedConv(conv)
    setMobileShowDetail(true)
    setLoadingDetail(true)
    try {
      const res = await customerApi.get(`/customer/inbox/conversations/${conv.id}`)
      setSelectedConv(res.data.conversation)
      setMessages(res.data.messages)
    } catch {
      toast.error('Failed to load conversation')
    } finally {
      setLoadingDetail(false)
    }
  }

  // Poll active conversation every 10s
  useEffect(() => {
    if (!selectedConv?.id) return
    detailPollRef.current = setInterval(async () => {
      try {
        const res = await customerApi.get(`/customer/inbox/conversations/${selectedConv.id}`)
        setMessages(res.data.messages)
      } catch {}
    }, 10000)
    return () => clearInterval(detailPollRef.current)
  }, [selectedConv?.id])

  const handleLoadMore = () => {
    const next = page + 1
    setPage(next)
    fetchConversations(next, true)
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setPage(1)
  }

  const handleSearch = (search) => {
    setFilters((prev) => ({ ...prev, search }))
    setPage(1)
  }

  const handleStatusChange = async (newStatus) => {
    if (!selectedConv) return
    try {
      await customerApi.put(`/customer/inbox/conversations/${selectedConv.id}/status`, { status: newStatus })
      toast.success(`Marked as ${newStatus}`)
      setSelectedConv((prev) => ({ ...prev, status: newStatus }))
      // Update in list
      setConversations((prev) =>
        prev.map((c) => (c.id === selectedConv.id ? { ...c, status: newStatus } : c))
      )
      fetchStats()
    } catch {
      toast.error('Failed to update status')
    }
  }

  // Checkbox
  const toggleCheck = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Bulk actions
  const handleBulkAction = async (action) => {
    if (action === 'delete') {
      setShowDeleteConfirm(true)
      return
    }
    try {
      await customerApi.post('/customer/inbox/conversations/bulk-action', {
        conversation_ids: Array.from(checkedIds),
        action,
      })
      toast.success(`${checkedIds.size} conversations ${action === 'resolve' ? 'resolved' : 'flagged'}`)
      setCheckedIds(new Set())
      fetchConversations(1)
      fetchStats()
    } catch {
      toast.error('Bulk action failed')
    }
  }

  const handleBulkDelete = async () => {
    try {
      await customerApi.post('/customer/inbox/conversations/bulk-action', {
        conversation_ids: Array.from(checkedIds),
        action: 'delete',
      })
      toast.success(`${checkedIds.size} conversations deleted`)
      setCheckedIds(new Set())
      setSelectedConv(null)
      setMessages([])
      setShowDeleteConfirm(false)
      fetchConversations(1)
      fetchStats()
    } catch {
      toast.error('Delete failed')
    }
  }

  const hasNoData = !loadingList && conversations.length === 0 && filters.status === 'all' && !filters.search

  return (
    <div className="h-[calc(100vh-130px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0 flex-wrap gap-2">
        <div className="flex items-center space-x-3">
          {mobileShowDetail && (
            <button
              onClick={() => setMobileShowDetail(false)}
              className="md:hidden p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </button>
          )}
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Inbox</h2>
            <p className="text-xs text-slate-400">
              {stats.total || 0} total · {stats.active || 0} active · {stats.today || 0} today
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => { fetchConversations(1); fetchStats() }}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Filters + Search */}
      <div className="flex items-center space-x-3 mb-4 flex-shrink-0 flex-wrap gap-2">
        <div className="w-56">
          <InboxSearch onSearch={handleSearch} />
        </div>
        <InboxFilters
          filters={filters}
          onChange={handleFilterChange}
          onClear={() => handleFilterChange(DEFAULT_FILTERS)}
        />
      </div>

      {hasNoData ? (
        <div className="flex-1 flex items-center justify-center bg-white rounded-xl border border-slate-200">
          <div className="text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <InboxIcon className="h-8 w-8 text-slate-300" />
            </div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">No conversations yet</h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto">
              Conversations will appear here when visitors use your chat widget.
            </p>
          </div>
        </div>
      ) : (
        /* Split Panel */
        <div className="flex-1 flex space-x-4 min-h-0">
          {/* Left panel - conversation list */}
          <div className={`w-full md:w-[35%] flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden ${
            mobileShowDetail ? 'hidden md:flex' : 'flex'
          }`}>
            <ConversationList
              conversations={conversations}
              selectedId={selectedConv?.id}
              onSelect={selectConversation}
              onLoadMore={handleLoadMore}
              hasMore={hasMore}
              isLoading={loadingList}
              checkedIds={checkedIds}
              onCheck={toggleCheck}
            />
          </div>

          {/* Right panel - detail */}
          <div className={`w-full md:w-[65%] flex flex-col min-h-0 ${
            !mobileShowDetail ? 'hidden md:flex' : 'flex'
          }`}>
            <ConversationDetail
              conversation={selectedConv}
              messages={messages}
              onStatusChange={handleStatusChange}
              loading={loadingDetail}
            />
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={checkedIds.size}
        onResolve={() => handleBulkAction('resolve')}
        onFlag={() => handleBulkAction('flag')}
        onDelete={() => handleBulkAction('delete')}
        onCancel={() => setCheckedIds(new Set())}
      />

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <ConfirmDialog
          title="Delete Conversations"
          message={`Are you sure you want to delete ${checkedIds.size} conversations? This cannot be undone.`}
          confirmText="Delete"
          onConfirm={handleBulkDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          destructive
        />
      )}
    </div>
  )
}
